import type { CancelTokenSource } from 'axios';
import axios from 'axios';
import { assign, fromPromise, sendTo, setup } from 'xstate';
import { createHttpClient } from './createHttpClient';
import type { PartForStateMachine as Part, UploaderMachineType } from './types';
import type Store from './UploaderStore';

type ParentActor = UploaderMachineType;

// In the first 8m+, the exponential backoff will trigger 8 attempts. In the
// following 23-point-something hours, it's an attempt every 5 mins:
//
// 8 + (24 * 60 / 5) = 296
//
// So rounding it up to a nice 300 which will put it over 24 hours for sure.
const maxRetryAttempts = 300;

// Define the sub-machine (represents processing one item)
export const partMachine = setup({
  types: {
    context: {} as {
      parentRef: ParentActor;
      store: Store;
      currentPart?: Part;
      currentCancelSource?: CancelTokenSource;
      isLoggingEnabled?: boolean;
    },
    input: {} as {
      parentRef: ParentActor;
      store: Store;
      isLoggingEnabled?: boolean;
    },
  },
  actors: {
    nextPartLookup: fromPromise(
      async ({
        input,
      }: {
        input: { store: Store };
      }): Promise<Part | undefined> => {
        if (!input?.store) {
          return Promise.resolve(undefined);
        }
        return Promise.resolve(input.store.getNextPart());
      }
    ),
    upload: fromPromise(
      ({
        input,
      }: {
        input: { part?: Part; store: Store; cancelSource?: CancelTokenSource };
      }) => {
        if (input && input.part && input.cancelSource) {
          const part = input.part;

          // perform part upload
          const { url, blob } = part;
          if (!url) {
            return Promise.reject(new Error('no url for part'));
          }

          // Create a new client every time just so that `onRetry` can be correlated to
          // the part's request.
          return createHttpClient({
            // Keep retrying until S3 upload url expires in 24 hours, at which point a
            // 403 error will be returned. With a 403 error, axios-retry will stop
            // retrying and emit the "error" event below.
            retries: maxRetryAttempts,
            onRetry: (retryCount) => {
              part.retryCount = retryCount;
            },
          })
            .put(url, blob, {
              cancelToken: input.cancelSource.token,
              headers: { 'content-type': blob.type },
              timeout: 0,
              onUploadProgress: (progressEvent) => {
                // Update progress for the part in the store
                input.store.setPartProgress(part, progressEvent);
              },
            })
            .then(() => {
              return part;
            });
        }
        return Promise.reject(new Error('no part to upload or cancel source'));
      }
    ),
  },
  guards: {
    hasFoundPart: ({ event }) => {
      return event.output !== undefined;
    },
    isUrlExpiredError: ({ event }) => {
      const error = event.error;
      // Check if it's an axios error and has the S3 error response
      if (axios.isAxiosError(error) && error.response?.status === 403) {
        // Parse the XML error response from S3
        const errorText = error.response.data;
        // Check if it contains the specific S3 expired request error message

        return (
          typeof errorText === 'string' &&
          errorText.includes('<Code>AccessDenied</Code>') &&
          errorText.includes('<Message>Request has expired</Message>')
        );
      }
      return false;
    },
    needsUrls: ({ context }) => {
      return context.store.getNextAdjacentPartsToFetch(1).length > 0;
    },
  },
  actions: {
    resetCurrent: assign({
      currentCancelSource: undefined,
      currentPart: undefined,
    }),
    createCancelSource: assign({
      currentCancelSource: () => axios.CancelToken.source(),
    }),
    removePartUrl: ({ context }) => {
      if (context.currentPart) {
        context.store.removePartUrl(context.currentPart);
      }
    },
    cancelCurrent: ({ context }) => {
      if (context.currentCancelSource) {
        context.currentCancelSource.cancel();
      }
    },
    completePart: ({ context, event }) => {
      context.store.completePart(event.output);
    },
    log: ({ context, self }, params: { message: string }) => {
      if (context.isLoggingEnabled) {
        // eslint-disable-next-line no-console
        console.log(
          `[PartUploader ${self.id.replace('part-machine-', '')}]`,
          params.message
        );
      }
    },
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAcCGAnALgWmeg9gMZyz7oB0ACgEoDyAwgKIDKzAkgHIDiAxBPgDsw5WJlSZhaLLgLFYpCjQYt23ANoAGALqIU+WAEtMBwbpAAPRAE4ATADZyGuwHYAzABYArDe8AOT+6+ADQgAJ6IAIyuVuS+VhFeCe5WGlbOdjYAvpkhUjh4RCRkVHRMrJy8zAAqtJSaOkggyPpGJgJmlgjx7uTRVr52yXZWngHuIeEI7hGe5O4+GjaBqb6uzu7ZuRj5skWKpSoV5ByMABpVAPqUAILUlwAytLQA0gCqlHyCwgYCAG74AGtJNsZIV5MUlGVVFxjmdLjc7hdHi93ggfv9COI2vV6mZmoZjKZGp0fDZyJ5fAknL4bFZXAFnBNrM5es5PM40lZAv0OZ5Nk0QQU5AoSspytxYecrrcHk83h9+EJyOjAcDpEK9qKoUcTlKEbKUZQ0X8iFjBDiIg09AS2h1EJ4XORhq4MiMRh4IkyEEsWQFbA7nFEInZVnycgL1btwfsxdDyO9HtcACKfJUqoHkPKg4UQg7imEJ2jJ40Ys0CHHaPEtQntYmIeZzENsinRdLeOyeL02fyxUbuaaUjQDNbZcMCfAQOB4wVRhRVm1E0CdbBWOxe7DOFlpebUum0jz8rMa6Naw7ceetRcWRDYQJemLODQaXneZy+dzOGzpQ8zsEiyFnjCurwjKSJyu8F41naCAaF6ERPuQVhcuyNiuBooyuDMVg-pGf65rGRyFsmkG2nWCCuB4jieCMtJ2BE8ERAMXqYQ4-iuL4b6ePBtJxDhOx4TG2oShwtAXAAsrQ1CMNKdzMCRV6dHRszshE6TNiMT6MmE9boU6wajEs9EaBEIx8dmmrVLU8m1kukToT0IZfquTg2K5wTad6XHkG4dLcv4SHsr4o6ZEAA */
  id: 'part-processor',
  initial: 'PROCESSING',
  context: ({ input }) => ({
    parentRef: input.parentRef,
    store: input.store,
    currentPart: undefined,
    isLoggingEnabled: input.isLoggingEnabled ?? false,
  }),
  states: {
    PROCESSING: {
      initial: 'NEXT_PART_LOOKUP',
      states: {
        NEXT_PART_LOOKUP: {
          invoke: {
            src: 'nextPartLookup',
            input: ({ context: { store } }) => ({ store }),
            onDone: [
              {
                guard: 'hasFoundPart',
                target: 'UPLOAD',
                actions: [
                  assign({ currentPart: ({ event }) => event.output }),
                  {
                    type: 'log',
                    params: {
                      message: 'Found new part',
                    },
                  },
                ],
              },
              {
                guard: 'needsUrls',
                target: 'WAITING_FOR_NEW_URL',
                actions: [
                  {
                    type: 'log',
                    params: {
                      message: 'No part found with URL',
                    },
                  },
                ],
              },
              {
                target: 'NO_MORE_PARTS',
                actions: [
                  {
                    type: 'log',
                    params: {
                      message: 'No more parts found',
                    },
                  },
                ],
              },
            ],
          },
        },
        UPLOAD: {
          entry: [
            'createCancelSource',
            {
              type: 'log',
              params: {
                message: 'Uploading part',
              },
            },
          ],
          invoke: {
            src: 'upload',
            input: ({ context }) => {
              return {
                part: context.currentPart,
                store: context.store,
                cancelSource: context.currentCancelSource,
              };
            },
            onDone: {
              target: 'NEXT_PART_LOOKUP',
              actions: [
                {
                  type: 'log',
                  params: {
                    message: 'Part upload completed',
                  },
                },
                'completePart',
                'resetCurrent',
                sendTo(({ context }) => context.parentRef, {
                  type: 'PART_UPLOAD_COMPLETE',
                }),
              ],
            },
            onError: [
              {
                guard: 'isUrlExpiredError',
                target: 'WAITING_FOR_NEW_URL',
                actions: [
                  {
                    type: 'log',
                    params: {
                      message: 'URL expired, requesting new URLs',
                    },
                  },
                  'removePartUrl',
                  'resetCurrent',
                ],
              },
              {
                target: '#part-processor.FAILED',
                actions: [
                  {
                    type: 'log',
                    params: ({ event }) => ({
                      message: `Error uploading part: ${JSON.stringify(
                        event.error
                      )}`,
                    }),
                  },
                  'resetCurrent',
                ],
              },
            ],
          },
        },
        WAITING_FOR_NEW_URL: {
          entry: [
            {
              type: 'log',
              params: {
                message: 'Waiting for new URL',
              },
            },
            sendTo(({ context }) => context.parentRef, {
              type: 'FETCH_NEW_URLS',
            }),
          ],
          on: {
            URL_UPDATED: {
              target: 'NEXT_PART_LOOKUP',
              actions: [
                {
                  type: 'log',
                  params: {
                    message: 'Received new URL, retrying upload',
                  },
                },
              ],
            },
          },
        },
        NO_MORE_PARTS: {
          entry: [
            {
              type: 'log',
              params: {
                message: 'No more parts to process',
              },
            },
          ],
          type: 'final',
        },
      },
      on: {
        STOP: 'STOP',
      },
      onDone: 'COMPLETE',
    },
    STOP: {
      entry: [
        {
          type: 'log',
          params: {
            message: 'Stopping machine',
          },
        },
        'cancelCurrent',
      ],
      type: 'final',
    },
    FAILED: {
      entry: [
        {
          type: 'log',
          params: {
            message: 'Part machine failed',
          },
        },
        'cancelCurrent',
        sendTo(
          ({ context }) => context.parentRef,
          ({ event }) => ({
            type: 'FAILED',
            error: event.error,
          })
        ),
      ],
      type: 'final',
    },
    COMPLETE: {
      entry: [
        {
          type: 'log',
          params: {
            message: 'Part machine completed',
          },
        },
        sendTo(({ context }) => context.parentRef, {
          type: 'PROCESS_DONE',
        }),
      ],
      type: 'final',
    },
  },
});
